import { useCallback } from 'react';

interface UseDialogCloseGuardOpts {
  /** Whether the dialog has unsaved changes. */
  isDirty: boolean;
  /** Called once the user has confirmed (or there were no unsaved changes). */
  onClose: () => void;
  /** Confirmation prompt message. */
  message?: string;
}

interface DialogCloseGuard {
  /**
   * Wire to `<Dialog onOpenChange>`: ignores the `true` direction and prompts
   * for unsaved changes on close. Returns true if the close proceeded.
   */
  handleOpenChange: (open: boolean) => boolean;
  /**
   * Wire to `<DialogContent onPointerDownOutside>` (and optionally
   * `onInteractOutside`). Blocks close-on-overlay-click outright — no prompt.
   */
  preventOutsideClose: (e: Event) => void;
}

/**
 * Standard close-behavior for form dialogs:
 *  - Background click is ignored (a click is too easy to fire by accident).
 *  - Escape / X-button / explicit cancel prompts the user when the form is dirty.
 *
 * Shape (with shadcn Dialog + react-hook-form):
 *
 *   const { handleOpenChange, preventOutsideClose } = useDialogCloseGuard({
 *     isDirty: form.formState.isDirty,
 *     onClose: () => setOpen(false),
 *   });
 *
 *   <Dialog open={open} onOpenChange={handleOpenChange}>
 *     <DialogContent onPointerDownOutside={preventOutsideClose}>
 *       …
 *       <Button onClick={() => handleOpenChange(false)}>Cancel</Button>
 *     </DialogContent>
 *   </Dialog>
 */
export function useDialogCloseGuard({
  isDirty,
  onClose,
  message = 'You have unsaved changes. Discard them?',
}: UseDialogCloseGuardOpts): DialogCloseGuard {
  const handleOpenChange = useCallback(
    (open: boolean): boolean => {
      if (open) return true;
      if (isDirty && !window.confirm(message)) return false;
      onClose();
      return true;
    },
    [isDirty, onClose, message]
  );

  const preventOutsideClose = useCallback((e: Event) => {
    e.preventDefault();
  }, []);

  return { handleOpenChange, preventOutsideClose };
}
